import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { LocaleService } from '@sm360/widget-commons-core-services';
import {
    AccountService,
    AnalyticsService,
    ProfileService,
    TRACKING_TAGS as TAGS,
    CONSTANT,
} from '@sm360/widget-commons-general';

import TradeInService from 'trade-in-360/services/TradeInService/TradeInService.js';
import TradeInModelNew from 'trade-in-360/model/TradeInModelNew.js';
import ErrorMessage from 'trade-in-360/components/ErrorMessage/ErrorMessage.jsx';

import 'trade-in-360/components/InstantEstimateWrapper/Steps/AppointmentForm/AppointmentForm.scss';
import { InputDate } from '@sm360/ui-library';
import { logger } from '@sm360/logger';

import SCHEDULE_TIME from '../../../../constants/scheduleTime.js';
import ActionButton from '../../Components/ActionButton/ActionButton.jsx';
import ConfirmationMessage from '../../Components/ConfirmationMessage/ConfirmationMessage.jsx';

const { APPOINTMENT_DATE_FORMAT } = CONSTANT || {};

const AppointmentForm = ({ tradeInVehicle, quote = undefined, onNextStep, closeModal, config }) => {
    const lang = LocaleService.getSimpleLocale();
    const [scheduleDate, setScheduleDate] = useState(new Date());
    const [scheduleTime, setScheduleTime] = useState(SCHEDULE_TIME.MORNING);
    const [tryVehicle, setTryVehicle] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [leadUpdated, setLeadUpdated] = useState(false);

    // Scroll to the top
    useEffect(() => {
        const modal = document.getElementById('webauto-trade-in-modal');
        if (modal) setTimeout(() => modal.scrollIntoView({ behavior: 'smooth' }), 200);
    }, []);

    const getQuoteFullName = (quoteVehicle) => {
        const vehicle = quoteVehicle?.vehicle;
        if (vehicle) {
            let vehicleName = '';
            if (lang !== 'fr' && vehicle?.year) vehicleName += `${vehicle.year}`;
            if (vehicle?.make) vehicleName += ` ${vehicle.make}`;
            if (vehicle?.model) vehicleName += ` ${vehicle.model}`;
            if (vehicle?.modelTrim) vehicleName += ` ${vehicle.modelTrim}`;
            if (lang === 'fr' && vehicle?.year) vehicleName += ` ${vehicle.year}`;

            return vehicleName.trim();
        }

        return LocaleService.t('TRADE_IN.UNKNOWN_VEHICLE');
    };

    const purchaseVehicleName = useMemo(() => getQuoteFullName(quote), [quote]);

    const sendAppointmentActivity = async (event) => {
        event.preventDefault();
        setLoading(true);
        let accountObject = {};
        try {
            const account = await ProfileService.fetchProfile();
            if (account) {
                accountObject = {
                    firstname: account?.firstname,
                    lastname: account?.lastname,
                    email: account?.email1,
                    homePhone: account?.homePhone,
                    optIn: account?.optInEmail,
                };
            }
        } catch (e) {
            logger.error('Error fetching the profile information', e);
        }

        const tradeInToSave = new TradeInModelNew({
            id: '', // TODO: Fill this for Edit tradeIn
            leadId: '', // TODO: Fill this for Edit tradeIn
            tradeInVehicle,
            estimation: {
                amount: tradeInVehicle?.estimation?.amount,
                amountWasUpdated: true,
                high: tradeInVehicle?.estimation?.amount,
                low: tradeInVehicle?.estimation?.amount,
            },
            profile: AccountService.isAuthenticated() ? accountObject : tradeInVehicle.contact,
            info: `${LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.LEAD_INFO', {
                date: scheduleDate.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-CA'),
                time: LocaleService.t(`TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.${scheduleTime}`),
            })}${
                quote
                    ? LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.LEAD_INFO_TRY_VEHICLE', {
                          vehicleName: purchaseVehicleName,
                          wantToTry: LocaleService.t(`TRADE_IN.BUTTONS.${tryVehicle ? 'YES' : 'NO'}`),
                      })
                    : ''
            }`,
        });

        TradeInService.saveTradeInNew(tradeInToSave, '', config)
            .then(() => {
                setLeadUpdated(true);
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    };

    const setTimeChange = (times) => {
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_APPOINTMENT_TIME_SELECTION(times.toString()));
        setScheduleTime(times);
    };

    const setDateChange = (date) => {
        const result = moment(date).format(APPOINTMENT_DATE_FORMAT);
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_APPOINTMENT_DATE_SELECTION(result));
        setScheduleDate(date);
    };

    return (
        <div className="appointment-form-dilawri">
            {leadUpdated ? (
                <ConfirmationMessage
                    title={LocaleService.t('TRADE_IN.THANK_YOU')}
                    description={LocaleService.t('TRADE_IN.SECTIONS_CONTENT.INSPECTION_NEEDED.CONFIRMATION')}
                    closeText={LocaleService.t('TRADE_IN.BUTTONS.KEEP_BROWSING')}
                    onClose={closeModal}
                />
            ) : (
                <form onSubmit={sendAppointmentActivity}>
                    <section className="appointment-form-dilawri--header">
                        {error && (
                            // Display an error box if lead creation or appraisal value request failed
                            <ErrorMessage />
                        )}
                        <div className="appointment-form-dilawri--header--title">
                            <icon className="icon-calendar" />
                            <h3>{LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.BOOK_TITLE')}</h3>
                        </div>
                        <div className="appointment-form-dilawri--header--vehicles">
                            {quote && (
                                <div className="appointment-form-dilawri--header--vehicles--purchase">
                                    <icon className="icon-review" />
                                    <span>{purchaseVehicleName}</span>
                                </div>
                            )}
                            <div className="appointment-form-dilawri--header--vehicles--tradein">
                                <icon className="icon-refresh" />
                                <span>{tradeInVehicle?.name}</span>
                            </div>
                        </div>
                    </section>
                    <section className="appointment-form-dilawri--schedule">
                        <div className="appointment-form-dilawri--schedule--date">
                            <span className="appointment-form-dilawri--labels">
                                {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.SCHEDULE_DESC')}
                            </span>
                            <InputDate
                                value={scheduleDate}
                                onChange={(date) => setDateChange(date)}
                                locale={lang}
                                calendarOnly
                            />
                        </div>
                        <div className="appointment-form-dilawri--schedule--time vehicle-buttons-container">
                            <div className="vehicle-buttons">
                                <ActionButton
                                    next
                                    noPrefix
                                    noArrow
                                    noSubmit
                                    selected={scheduleTime === SCHEDULE_TIME.MORNING}
                                    onClick={() => setTimeChange(SCHEDULE_TIME.MORNING)}
                                >
                                    {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.MORNING')}
                                </ActionButton>
                                <ActionButton
                                    next
                                    noPrefix
                                    noArrow
                                    noSubmit
                                    selected={scheduleTime === SCHEDULE_TIME.AFTERNOON}
                                    onClick={() => setTimeChange(SCHEDULE_TIME.AFTERNOON)}
                                >
                                    {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.AFTERNOON')}
                                </ActionButton>
                                <ActionButton
                                    next
                                    noPrefix
                                    noArrow
                                    noSubmit
                                    selected={scheduleTime === SCHEDULE_TIME.EVENING}
                                    onClick={() => setTimeChange(SCHEDULE_TIME.EVENING)}
                                >
                                    {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.EVENING')}
                                </ActionButton>
                            </div>
                        </div>
                    </section>
                    {quote && (
                        <section className="appointment-form-dilawri--try-vehicle">
                            <span className="appointment-form-dilawri--labels">
                                {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.TRY_DESC', {
                                    vehicleName: purchaseVehicleName,
                                })}
                            </span>
                            <div className="appointment-form-dilawri--try-vehicle--buttons vehicle-buttons-container">
                                <div className="vehicle-buttons">
                                    <ActionButton
                                        next
                                        noPrefix
                                        noArrow
                                        noSubmit
                                        selected={tryVehicle}
                                        onClick={() => setTryVehicle(true)}
                                    >
                                        {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.TRY_YES')}
                                    </ActionButton>
                                    <ActionButton
                                        next
                                        noPrefix
                                        noArrow
                                        noSubmit
                                        selected={!tryVehicle}
                                        onClick={() => setTryVehicle(false)}
                                    >
                                        {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.APPOINTMENT_FORM.TRY_NO')}
                                    </ActionButton>
                                </div>
                            </div>
                        </section>
                    )}
                    <section className="clearfix vehicle-buttons-container" data-test="trade-in.vehicle-form.submit">
                        <div className="vehicle-buttons">
                            <ActionButton next noPrefix onClick={onNextStep} loading={loading}>
                                {LocaleService.t('TRADE_IN.BUTTONS.MAKE_APPOINTMENT')}
                            </ActionButton>
                        </div>
                    </section>
                </form>
            )}
        </div>
    );
};

AppointmentForm.defaultProps = {
    config: {},
};

AppointmentForm.propTypes = {
    tradeInVehicle: PropTypes.object,
    quote: PropTypes.object,
    onNextStep: PropTypes.func,
    config: PropTypes.object,
};

AppointmentForm.displayName = 'AppointmentForm';

export default AppointmentForm;
